import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiFetch, ApiClientError } from '@/lib/api';
import { AlertTriangle } from 'lucide-react';
import { toast } from '@/stores/toast';
import { Button } from '@/components/Button';
import { ConfirmDialog } from '@/components/Modal';
import { NodeMultiSelect } from '@/components/NodeMultiSelect';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import type { NodeTreeItem, NodesResponse } from '@/stores/scope';

/**
 * 社員の所属編集 (v0.3 §2-6 の dry-run/confirm 2phase を踏襲)。
 *
 * ⚠️ EmployeesPage の所属編集モーダルと EmployeeDetailPage の所属タブに同じ UI が二重実装されて
 * おり、片方だけ直すと §8-55 型の同期漏れになるため 1 コンポーネントへ集約した。
 *
 * v0.4 追加要件⑩:
 *  - 選択肢をレガシー `stores` から組織ノードツリーへ変更した (本部等の非店舗ノードにも所属できる、D-324)
 *  - 所属ごとに役職・等級を設定できるようにした (§2-4: 書く側が存在せず D-11 の可視性ルールが
 *    全員 false になっていた穴を塞ぐ)
 *  - 所属ごとに種別 (通常勤務 / 応援可能) を設定できるようにした (D-325)。応援 (support) は
 *    ロースターの既定表示から外れ「応援候補」枠に出る (ShiftBoardPage 側)。
 */

type Assignment = {
  node_id: string; is_primary: boolean; position_level_id: string | null; position_level_label: string | null;
  assignment_kind: 'work' | 'support';
};
type CurrentResponse = { employee_id: string; assignments: Assignment[] };
type PositionLevel = { position_level_id: string; label: string; sort_order: number };

type NodeAssignmentPreviewUser = {
  user_id: string; display_name: string; login_id: string;
  current_primary_node_id: string | null; target_primary_node_id: string | null;
};
type NodeAssignmentResult = {
  employee_id: string; dry_run: boolean; sync_mode: 'off' | 'overwrite' | 'append';
  affected_users?: NodeAssignmentPreviewUser[]; permission_skipped_user_ids?: string[];
  updated_user_ids?: string[]; skipped_user_ids?: string[];
  standing_pattern_warning_node_ids?: string[];
};

const NO_LEVEL = '__none__';

const flattenNodes = (nodes: NodeTreeItem[]): NodeTreeItem[] =>
  nodes.flatMap((n) => [n, ...flattenNodes(n.children)]);

export function EmployeeAffiliationEditor({ employeeId, onSaved }: { employeeId: string; onSaved?: () => void }) {
  const qc = useQueryClient();

  const { data: nodesData } = useQuery<NodesResponse>({ queryKey: ['nodes'], queryFn: () => apiFetch<NodesResponse>('/nodes') });
  // ⚠️ /masters/* は requireEdition('full') + skill:view が要る (skill-masters.ts:41,44)。
  // Lite テナントや skill:view を持たない操作者では 403 になるため、「未登録」と区別して扱う
  // (区別しないと「マスタが未登録です」という誤った案内を出してしまう)。retry:false は
  // 403 に対する無駄な再試行を避けるため。
  const { data: positionLevels = [], isError: positionLevelsUnavailable } = useQuery<PositionLevel[]>({
    queryKey: ['position-levels'],
    queryFn: () => apiFetch<PositionLevel[]>('/masters/position-levels'),
    retry: false,
  });
  const { data: current, isLoading, isError } = useQuery<CurrentResponse>({
    queryKey: ['employee-node-assignments', employeeId],
    queryFn: () => apiFetch<CurrentResponse>(`/employees/${employeeId}/node-assignments`),
    retry: false,
  });

  const flatNodes = useMemo(() => flattenNodes(nodesData?.roots ?? []), [nodesData]);
  const nodeById = useMemo(() => new Map(flatNodes.map((n) => [n.node_id, n])), [flatNodes]);

  const [selectedIds, setSelectedIds] = useState<Set<string> | null>(null);
  const [primaryNodeId, setPrimaryNodeId] = useState<string | null>(null);
  const [levelByNodeId, setLevelByNodeId] = useState<Record<string, string | null>>({});
  const [kindByNodeId, setKindByNodeId] = useState<Record<string, 'work' | 'support'>>({});
  const [standingPatternWarningNodeIds, setStandingPatternWarningNodeIds] = useState<string[]>([]);
  // 役職一覧を取得できない場合でも保存済みの役職名は表示する (Select は候補に無い ID を空表示に
  // するため、無効化しただけだと「役職なし」と誤読される)
  const savedLevelLabelByNodeId = useMemo(
    () => Object.fromEntries((current?.assignments ?? []).map((a) => [a.node_id, a.position_level_label])),
    [current],
  );
  const [pendingSync, setPendingSync] = useState<{ result: NodeAssignmentResult; excludedUserIds: string[] } | null>(null);

  useEffect(() => {
    if (!current) return;
    setSelectedIds(new Set(current.assignments.map((a) => a.node_id)));
    // 2026-09-17 user実機報告: 所属が1件しかないのに主所属が未設定のまま保存され、
    // 「配下メンバーのスキル確認」等 is_primary=1 を前提とする画面が該当社員を一切表示しない
    // 事故が発生した(ラジオボタンを明示的にクリックしないと主所属が付かない仕様だったため)。
    // 既存データがこの状態のまま開かれた場合の自己修復として、所属1件なら自動的に主所属扱いにする。
    const existingPrimary = current.assignments.find((a) => a.is_primary)?.node_id;
    setPrimaryNodeId(existingPrimary ?? (current.assignments.length === 1 ? current.assignments[0]!.node_id : null));
    setLevelByNodeId(Object.fromEntries(current.assignments.map((a) => [a.node_id, a.position_level_id])));
    setKindByNodeId(Object.fromEntries(current.assignments.map((a) => [a.node_id, a.assignment_kind])));
  }, [current]);

  // ⚠️ QA HIGH: 選択集合をそのまま送ると、ツリーに出てこない停止済みノードへの既存所属が
  // 一緒に送られ verifyActiveNodesInTenant で 403 になり「保存が常に失敗して外せない」状態になる。
  // 画面の警告文どおり、ツリーに存在するノードだけを送って停止済み所属は解除する。
  const submittableNodes = flatNodes.filter((n) => selectedIds?.has(n.node_id) ?? false);

  const submitMut = useMutation({
    mutationFn: (params: { confirm?: boolean; excluded_user_ids?: string[] }) =>
      apiFetch<NodeAssignmentResult>(`/employees/${employeeId}/node-assignments`, {
        method: 'PATCH',
        body: JSON.stringify({
          assignments: submittableNodes.map((n) => ({
            node_id: n.node_id,
            position_level_id: levelByNodeId[n.node_id] ?? null,
            assignment_kind: kindByNodeId[n.node_id] ?? 'work',
          })),
          // 停止済みノードが主所属だった場合、送信対象から外れるため主所属も落とす (400 の回避)
          primary_node_id: submittableNodes.some((n) => n.node_id === primaryNodeId) ? primaryNodeId : null,
          ...params,
        }),
      }),
    onSuccess: (res) => {
      if (res.dry_run) { setPendingSync({ result: res, excludedUserIds: [] }); return; }
      qc.invalidateQueries({ queryKey: ['employee-node-assignments', employeeId] });
      qc.invalidateQueries({ queryKey: ['employees'] });
      setPendingSync(null);
      setStandingPatternWarningNodeIds(res.standing_pattern_warning_node_ids ?? []);
      const updatedCount = res.updated_user_ids?.length ?? 0;
      toast.success(updatedCount > 0 ? `所属を更新し、${updatedCount}名の連携ユーザーに反映しました` : '所属を更新しました');
      onSaved?.();
    },
    onError: (e) => toast.error(e instanceof ApiClientError ? e.message : '所属の更新に失敗しました'),
  });

  if (isLoading) return <div className="text-text-sub text-sm">読み込み中…</div>;
  // ⚠️ isError を無視すると nodeIds=null のまま「読み込み中…」で永久に固まる (design-expert 指摘の再発防止)
  if (isError || selectedIds === null) {
    return <p className="text-sm text-text-sub">所属情報の取得に失敗しました。権限をご確認のうえ、ページを再読み込みしてください。</p>;
  }

  // ツリー順を保った選択済みノード一覧 (Set の反復順ではなく組織構造の順に並べる)
  const selectedNodes = submittableNodes;
  // ツリーに存在しない (閉鎖済み等の) 所属も編集画面から消さずに警告として残す
  const orphanIds = [...selectedIds].filter((id) => !nodeById.has(id));

  const handleSelectionChange = (next: Set<string>) => {
    setSelectedIds(next);
    if (primaryNodeId && !next.has(primaryNodeId)) setPrimaryNodeId(null);
    // 所属が1件だけならラジオボタンを押さなくても主所属として扱う (押し忘れによる
    // is_primary未設定を構造的に防ぐ、上のuseEffectと同じ意図)
    if (next.size === 1) setPrimaryNodeId([...next][0]!);
  };

  return (
    <div className="space-y-4">
      <div>
        <label className="block text-sm text-text-sub mb-1">所属先（複数選択可）</label>
        <NodeMultiSelect nodes={nodesData?.roots ?? []} selectedIds={selectedIds} onChange={handleSelectionChange} />
      </div>

      {orphanIds.length > 0 && (
        <div role="alert" className="rounded border border-alert-border bg-alert-bg px-3 py-2 flex items-start gap-2">
          <AlertTriangle size={16} className="text-alert-text shrink-0 mt-0.5" aria-hidden />
          <p className="text-sm text-alert-text">
            稼働していない組織への所属が {orphanIds.length} 件あります。このまま保存すると解除されます。
          </p>
        </div>
      )}

      {standingPatternWarningNodeIds.length > 0 && (
        <div role="alert" className="rounded border border-alert-border bg-alert-bg px-3 py-2 flex items-start gap-2">
          <AlertTriangle size={16} className="text-alert-text shrink-0 mt-0.5" aria-hidden />
          <p className="text-sm text-alert-text">
            通常勤務から応援に変更した以下の所属には、恒常パターン（週間カバレッジ）がまだ残っています。
            不要であれば「週間カバレッジ」画面で個別に削除してください：
            {' '}
            {standingPatternWarningNodeIds.map((nid) => nodeById.get(nid)?.name ?? nid).join('、')}
          </p>
        </div>
      )}

      {selectedNodes.length > 0 && (
        <fieldset>
          <legend className="block text-sm text-text-sub mb-1">主所属・役職・種別</legend>
          <div className="border border-surface-border rounded divide-y divide-surface-border max-h-[40vh] overflow-y-auto">
            {selectedNodes.map((n) => (
              <div key={n.node_id} className="flex flex-wrap items-center gap-x-3 gap-y-2 p-2">
                <label className="flex items-center gap-2 text-sm min-h-[44px] flex-1 min-w-[10rem] cursor-pointer">
                  <input type="radio" name={`primary-node-${employeeId}`} className="w-4 h-4 shrink-0"
                    checked={primaryNodeId === n.node_id}
                    onChange={() => setPrimaryNodeId(n.node_id)}
                    aria-label={`${n.name} を主所属にする`}
                  />
                  <span className="truncate">{n.name}</span>
                  <span className="text-[11px] text-text-sub shrink-0">{n.node_type_code}</span>
                </label>
                <Select
                  value={kindByNodeId[n.node_id] ?? 'work'}
                  onValueChange={(v) => setKindByNodeId((prev) => ({ ...prev, [n.node_id]: v as 'work' | 'support' }))}
                >
                  <SelectTrigger className="w-32 min-h-[44px] shrink-0" aria-label={`${n.name} での所属種別`}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="work">通常勤務</SelectItem>
                    <SelectItem value="support">応援可能</SelectItem>
                  </SelectContent>
                </Select>
                {positionLevelsUnavailable ? (
                  <span className="w-44 shrink-0 text-sm text-text-sub">
                    {savedLevelLabelByNodeId[n.node_id] ?? '役職なし'}
                  </span>
                ) : (
                  <Select
                    value={levelByNodeId[n.node_id] ?? NO_LEVEL}
                    onValueChange={(v) => setLevelByNodeId((prev) => ({ ...prev, [n.node_id]: v === NO_LEVEL ? null : v }))}
                  >
                    <SelectTrigger className="w-44 min-h-[44px] shrink-0" aria-label={`${n.name} での役職・等級`}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value={NO_LEVEL}>役職なし</SelectItem>
                      {positionLevels.map((p) => (
                        <SelectItem key={p.position_level_id} value={p.position_level_id}>{p.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              </div>
            ))}
          </div>
          {positionLevelsUnavailable && (
            <p className="mt-1 text-sm text-text-sub">
              役職・等級の一覧を取得できませんでした（この機能の利用権限が無いか、対象外のプランです）。所属先の変更は保存できます。
            </p>
          )}
          {!positionLevelsUnavailable && positionLevels.length === 0 && (
            <p className="mt-1 text-sm text-text-sub">
              役職・等級のマスタが未登録です。「マスタ管理 &gt; スキル・人材像」で登録すると選べるようになります。
            </p>
          )}
        </fieldset>
      )}

      <Button
        variant="primary"
        loading={submitMut.isPending}
        loadingLabel="確認中…"
        onClick={() => {
          // 所属が1件以上あるのに主所属が未選択のまま保存すると is_primary=1 の行が0件になり、
          // 「配下メンバーのスキル確認」等の画面から該当社員が丸ごと見えなくなる (2026-09-17実害)。
          // 所属1件時は上のhandleSelectionChange/useEffectで自動設定されるため、ここに到達するのは
          // 複数所属で主所属を選び忘れたケースのみ。
          if (selectedNodes.length > 0 && !primaryNodeId) {
            toast.error('主所属を1つ選択してください');
            return;
          }
          submitMut.mutate({});
        }}
      >
        保存
      </Button>

      <ConfirmDialog
        open={pendingSync !== null}
        title="連携ユーザーへの反映を確認"
        confirmLabel="この内容で反映する"
        cancelLabel="保存をやめる"
        loading={submitMut.isPending}
        onClose={() => { if (!submitMut.isPending) setPendingSync(null); }}
        onConfirm={() => submitMut.mutate({ confirm: true, excluded_user_ids: pendingSync?.excludedUserIds ?? [] })}
        message={pendingSync && (
          <div className="space-y-3">
            <p className="text-sm">
              現在の連動モード「{pendingSync.result.sync_mode === 'overwrite' ? '上書き連動' : '追加連動'}」により、
              以下の連携ユーザーの所属も自動的に更新されます。除外したいユーザーはチェックを外してください。
            </p>
            <div className="border border-surface-border rounded max-h-56 overflow-y-auto divide-y divide-surface-border">
              {(pendingSync.result.affected_users ?? []).map((u) => (
                <label key={u.user_id} className="flex items-center gap-2 p-2 text-sm min-h-[44px]">
                  <input type="checkbox" className="w-4 h-4 shrink-0"
                    checked={!pendingSync.excludedUserIds.includes(u.user_id)}
                    onChange={(e) => {
                      const next = e.target.checked
                        ? pendingSync.excludedUserIds.filter((id) => id !== u.user_id)
                        : [...pendingSync.excludedUserIds, u.user_id];
                      setPendingSync({ ...pendingSync, excludedUserIds: next });
                    }}
                  />
                  <span className="flex-1 min-w-0">
                    <span className="font-medium">{u.display_name}</span>
                    <span className="text-text-sub font-mono text-xs ml-2">{u.login_id}</span>
                  </span>
                </label>
              ))}
              {(pendingSync.result.affected_users ?? []).length === 0 && (
                <p className="p-2 text-sm text-text-sub">連動対象のユーザーはいません。</p>
              )}
            </div>
          </div>
        )}
      />
    </div>
  );
}
